import { fetchReturnValuations, ReturnValuationsResponse, ReturnValuationData } from 'services/returnValuationsApi';
import {
  fetchPortfolioBalances,
  PortfolioBalancesResponse,
} from 'views/Portfolio/components/Summary/services/fetchPortfolioBalances';
import { PortfolioBalance } from 'types/DomainTypes';
import { ToolExecutionResult } from '../types';
import { formatCurrency, formatPercentage } from '../utils/formatters';

interface ReturnValuationSummary {
  portfolioName: string;
  portfolioId: string;
  valuationDate: string;
  valuationStart: string;
  valuationEnd: string;
  purchased: string;
  sold: string;
  netCapitalInvested: string;
  cumNetCapitalInvested: string;
  inFlow: string;
  outFlow: string;
  netPosition: string;
  netPositionPercent: string;
  oneMonthHoldingChange: string;
  oneMonthHoldingReturn: string;
  rank: number;
}

interface PortfolioOption {
  portfolioId: string;
  portfolioName: string;
}

interface UserAccountContext {
  accountHolderId: number;
  portfolios: PortfolioOption[];
}

// Module-level cache for accountHolderId (persists across tool calls within session)
let cachedAccountContext: UserAccountContext | null = null;

const formatNullableCurrency = (value: number | null): string => {
  if (value === null) return 'N/A';
  return formatCurrency(value);
};

const formatNullablePercentage = (value: number | null): string => {
  if (value === null) return 'N/A';
  return formatPercentage(value);
};

const transformValuationData = (data: ReturnValuationData): ReturnValuationSummary => ({
  portfolioName: data.portfolioName,
  portfolioId: data.portfolioId,
  valuationDate: data.valuationDate,
  valuationStart: formatNullableCurrency(data.valuationStart),
  valuationEnd: formatNullableCurrency(data.valuationEnd),
  purchased: formatNullableCurrency(data.purchased),
  sold: formatNullableCurrency(data.sold),
  netCapitalInvested: formatCurrency(data.netCapitalInvested),
  cumNetCapitalInvested: formatCurrency(data.cumNetCapitalInvested),
  inFlow: formatCurrency(data.inFlow),
  outFlow: formatCurrency(data.outFlow),
  netPosition: formatNullableCurrency(data.netPosition),
  netPositionPercent: formatNullablePercentage(data.totalNetPositionPercent),
  oneMonthHoldingChange: formatCurrency(data.oneMonthHoldingChange),
  oneMonthHoldingReturn: formatNullablePercentage(data.oneMonthHoldingReturn),
  rank: data.rank,
});

/**
 * Fetches user account context (accountHolderId and portfolios) from PortfolioStatements.
 * Caches the result for future calls within the same session.
 */
const getUserAccountContext = async (): Promise<UserAccountContext | null> => {
  // Return cached context if available
  if (cachedAccountContext) {
    return cachedAccountContext;
  }

  try {
    const response: PortfolioBalancesResponse = await fetchPortfolioBalances();
    const rawData: PortfolioBalance[] = response?.data || [];

    if (rawData.length === 0) {
      return null;
    }

    // Extract accountHolderId from the first record that has it
    const accountHolderId = rawData.find((p) => p.accountHolderId)?.accountHolderId;

    if (!accountHolderId) {
      return null;
    }

    // Deduplicate portfolios by portfolioId, excluding the "All Portfolios" entry (null portfolioId)
    const seenIds = new Set<string>();
    const portfolios = rawData
      .filter((p) => {
        // Skip entries with null portfolioId (the "All Portfolios" summary)
        if (!p.portfolioId || seenIds.has(p.portfolioId)) {
          return false;
        }
        seenIds.add(p.portfolioId);
        return true;
      })
      .map((p) => ({
        portfolioId: p.portfolioId || '',
        portfolioName: p.portfolioName || 'Portfolio',
      }));

    // Cache the context for future calls
    cachedAccountContext = {
      accountHolderId,
      portfolios,
    };

    return cachedAccountContext;
  } catch {
    return null;
  }
};

/**
 * Clears the cached account context (useful for testing or logout)
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const clearAccountContextCache = (): void => {
  cachedAccountContext = null;
};

export const executeGetReturnValuations = async (args: Record<string, unknown>): Promise<ToolExecutionResult> => {
  try {
    const portfolioId = args.portfolioId as string | undefined;
    const valuationDate = args.valuationDate as string | undefined;
    const page = args.page as number | undefined;
    const pageSize = args.pageSize as number | undefined;

    // Get user's account context (accountHolderId + portfolios)
    const accountContext = await getUserAccountContext();

    if (!accountContext) {
      return {
        success: false,
        error: 'Unable to retrieve account information. Please ensure you are logged in.',
      };
    }

    const { accountHolderId, portfolios: userPortfolios } = accountContext;

    // If no portfolioId provided and user has multiple portfolios, return portfolio options
    if (!portfolioId && userPortfolios.length > 1) {
      return {
        success: true,
        data: {
          requiresSelection: true,
          message: 'Please specify which portfolio you would like return valuations for:',
          availablePortfolios: [{ portfolioId: 'all', portfolioName: 'All Portfolios' }, ...userPortfolios],
          note: 'You can select a specific portfolio or "All Portfolios" to see combined data.',
        },
      };
    }

    // If user has only one portfolio and none specified, use that one
    const effectivePortfolioId =
      !portfolioId && userPortfolios.length === 1 ? userPortfolios[0].portfolioId : portfolioId;

    // Determine if we should send portfolioId (don't send for 'all', empty, or null)
    const shouldFilterByPortfolio =
      effectivePortfolioId && effectivePortfolioId !== 'all' && effectivePortfolioId.trim() !== '';

    const response: ReturnValuationsResponse = await fetchReturnValuations({
      acctHolderId: accountHolderId,
      portfolioId: shouldFilterByPortfolio ? effectivePortfolioId : undefined,
      valuationDate,
      page,
      pageSize,
    });

    const rawData: ReturnValuationData[] = response?.data || [];

    if (rawData.length === 0) {
      return {
        success: true,
        data: {
          message: 'No return valuation data available for the specified criteria.',
          valuations: [],
          totalRecords: 0,
        },
      };
    }

    const valuations: ReturnValuationSummary[] = rawData.map(transformValuationData);

    // Calculate aggregate statistics
    const aggregates = {
      totalPurchased: rawData.reduce((sum, v) => sum + (v.purchased || 0), 0),
      totalSold: rawData.reduce((sum, v) => sum + (v.sold || 0), 0),
      totalInFlow: rawData.reduce((sum, v) => sum + v.inFlow, 0),
      totalOutFlow: rawData.reduce((sum, v) => sum + v.outFlow, 0),
      totalNetCapitalInvested: rawData.reduce((sum, v) => sum + v.netCapitalInvested, 0),
      averageOneMonthHoldingChange: rawData.reduce((sum, v) => sum + v.oneMonthHoldingChange, 0) / rawData.length,
    };

    // Get date range if multiple records
    const sortedByDate = [...rawData].sort(
      (a, b) => new Date(a.valuationDate).getTime() - new Date(b.valuationDate).getTime(),
    );
    const dateRange =
      rawData.length > 1
        ? {
            earliest: sortedByDate[0].valuationDate,
            latest: sortedByDate[sortedByDate.length - 1].valuationDate,
          }
        : null;

    // Single valuation response
    if (valuations.length === 1) {
      const v = valuations[0];
      return {
        success: true,
        data: {
          portfolioName: v.portfolioName,
          portfolioId: v.portfolioId,
          valuationDate: v.valuationDate,
          valuationStart: v.valuationStart,
          valuationEnd: v.valuationEnd,
          purchased: v.purchased,
          sold: v.sold,
          netCapitalInvested: v.netCapitalInvested,
          cumulativeNetCapitalInvested: v.cumNetCapitalInvested,
          inFlow: v.inFlow,
          outFlow: v.outFlow,
          netPosition: v.netPosition,
          netPositionPercent: v.netPositionPercent,
          oneMonthHoldingChange: v.oneMonthHoldingChange,
          oneMonthHoldingReturn: v.oneMonthHoldingReturn,
          rank: v.rank,
        },
      };
    }

    // Multiple valuations response (time series or multiple portfolios)
    const uniquePortfolios = [...new Set(rawData.map((v) => v.portfolioName))];
    const isMultiplePortfolios = uniquePortfolios.length > 1;

    return {
      success: true,
      data: {
        portfolioName: isMultiplePortfolios ? 'All Portfolios' : valuations[0]?.portfolioName || 'Unknown',
        valuations,
        totalRecords: response.totalRecords,
        dateRange,
        aggregates: {
          totalPurchased: formatCurrency(aggregates.totalPurchased),
          totalSold: formatCurrency(aggregates.totalSold),
          totalInFlow: formatCurrency(aggregates.totalInFlow),
          totalOutFlow: formatCurrency(aggregates.totalOutFlow),
          totalNetCapitalInvested: formatCurrency(aggregates.totalNetCapitalInvested),
          averageOneMonthHoldingChange: formatCurrency(aggregates.averageOneMonthHoldingChange),
        },
        note: `Showing ${valuations.length} valuation records${
          dateRange ? ` from ${dateRange.earliest} to ${dateRange.latest}` : ''
        }${isMultiplePortfolios ? ` across ${uniquePortfolios.length} portfolios` : ''}.`,
      },
    };
  } catch (error) {
    const err = error as Error & { response?: { status?: number; data?: unknown } };
    let errorMessage = err.message || 'Unknown error';

    if (err.response?.status === 401) {
      errorMessage = 'Authentication required. Please ensure you are logged in.';
    } else if (err.response?.status === 403) {
      errorMessage = 'Access denied to return valuation data.';
    } else if (err.response?.status === 404) {
      errorMessage = 'Return valuation data not found for the specified criteria.';
    } else if (err.message?.includes('Network Error')) {
      errorMessage = 'Network error. Please check your connection.';
    }

    return {
      success: false,
      error: `Failed to fetch return valuations: ${errorMessage}`,
    };
  }
};
